import {
  SMPSDevice,
  SMPSStats,
  DeviceStatus,
} from './smpsTypes';

// SMPS Brand Colors
export const SMPS_COLORS = {
  primary: '#1a5276',
  secondary: '#0e3d5a',
  accent: '#f39c12',
  statusHealthy: '#27ae60',
  statusWarning: '#f39c12',
  statusCritical: '#e74c3c',
  statusOffline: '#95a5a6',
  bgColor: '#f8fafc',
  cardBg: '#ffffff',
  textColor: '#1e293b',
  textSecondary: '#64748b',
  borderColor: '#e2e8f0',
  hoverColor: '#f1f5f9',
};

// Voltage Thresholds (for 48V system)
export const VOLTAGE_THRESHOLDS = {
  nominal: 48,
  highWarning: 54,
  highCritical: 56,
  lowWarning: 44,
  lowCritical: 42,
};

// Default station list
export const DEFAULT_STATIONS = ['ALL'];

// Helper functions
export const calculateStats = (devices: SMPSDevice[]): SMPSStats => {
  let healthy = 0;
  let warning = 0;
  let critical = 0;
  let offline = 0;
  let totalEfficiency = 0;
  let totalLoad = 0;
  let onlineCount = 0;

  devices.forEach((device) => {
    switch (device.status) {
      case 'HEALTHY':
        healthy++;
        break;
      case 'WARNING':
        warning++;
        break;
      case 'CRITICAL':
        critical++;
        break;
      case 'OFFLINE':
        offline++;
        break;
    }

    if (device.communicationStatus === 'ONLINE') {
      totalEfficiency += device.efficiency;
      totalLoad += device.loadCurrent;
      onlineCount++;
    }
  });

  return {
    totalDevices: devices.length,
    healthy,
    warning,
    critical,
    offline,
    avgEfficiency: onlineCount > 0 ? parseFloat((totalEfficiency / onlineCount).toFixed(1)) : 0,
    totalLoad: parseFloat(totalLoad.toFixed(1)),
  };
};

export const getDeviceHealth = (device: SMPSDevice): DeviceStatus => {
  if (device.communicationStatus === 'OFFLINE') return 'OFFLINE';
  if (device.alarms.activeCount > 2 || device.alarms.lvdStatus) return 'CRITICAL';
  if (device.dcVoltage < VOLTAGE_THRESHOLDS.lowCritical || device.dcVoltage > VOLTAGE_THRESHOLDS.highCritical) return 'CRITICAL';
  if (device.alarms.activeCount > 0) return 'WARNING';
  if (device.dcVoltage < VOLTAGE_THRESHOLDS.lowWarning || device.dcVoltage > VOLTAGE_THRESHOLDS.highWarning) return 'WARNING';
  if (device.rectifiers.online < device.rectifiers.total) return 'WARNING';
  return 'HEALTHY';
};

export const getHealthColor = (status: DeviceStatus): string => {
  switch (status) {
    case 'HEALTHY':
      return SMPS_COLORS.statusHealthy;
    case 'WARNING':
      return SMPS_COLORS.statusWarning;
    case 'CRITICAL':
      return SMPS_COLORS.statusCritical;
    case 'OFFLINE':
      return SMPS_COLORS.statusOffline;
    default:
      return SMPS_COLORS.statusOffline;
  }
};

export const getStatusLabel = (status: DeviceStatus): string => {
  switch (status) {
    case 'HEALTHY':
      return 'Healthy';
    case 'WARNING':
      return 'Warning';
    case 'CRITICAL':
      return 'Critical';
    case 'OFFLINE':
      return 'Offline';
    default:
      return SMPS_COLORS.statusOffline;
  }
};

export const getBatteryModeLabel = (mode: string): string => {
  switch (mode) {
    case 'FLOAT':
      return 'Float Charge';
    case 'BOOST':
      return 'Boost Charge';
    case 'DISCHARGE':
      return 'Discharging';
    case 'TEST':
      return 'Test Mode';
    default:
      return 'Unknown';
  }
};
